// @ts-strict-ignore
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { PaginationInfo } from '@/actions/pagination';
import type { RootState } from '@/reducers/reducers';
import { selectIsAppsDemoModeEnabled, selectRelease, selectReleasesForAppId } from '@/selectors/selectors';
import {
    useGetTimelineEventsHistoryQuery,
    useGetTimelineEventsQuery,
    useLazyGetTimelineEventsHistoryQuery,
    useLazyGetTimelineEventsQuery,
} from '../../middlewares/paginationApi';
import type { ReleaseStepType, TimelineEvent } from '../../types';

interface UseTimelineEventsPaginationProps {
    orgId: string;
    appId: string;
    releaseId?: string;
    releaseStepType?: ReleaseStepType;
    workflowId?: string;
    workflowRunId?: string;
    stepRunIds?: string[];
    initialLoad?: boolean;
}

interface UseTimelineEventsPaginationReturn {
    events: TimelineEvent[];
    paginationInfo: PaginationInfo;
    isLoading: boolean;
    isLoadingMore: boolean;
    error: boolean;
    errorMessage?: string;
    loadMore: () => void;
    refresh: () => void;
    reset: () => void;
}

export const useTimelineEventsPagination = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    workflowId,
    workflowRunId,
    stepRunIds,
    initialLoad = true,
}: UseTimelineEventsPaginationProps): UseTimelineEventsPaginationReturn => {
    const [allEvents, setAllEvents] = useState<TimelineEvent[]>([]);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>(null);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const isWorkflowsRequest = !!(workflowId?.length && stepRunIds?.length);
    const [success, setSuccess] = useState<boolean>(true);
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const appReleases = useSelector((state: RootState) => selectReleasesForAppId(state, appId), shallowEqual);

    const baseRequest = useMemo(
        () => ({
            orgId,
            appId,
            releaseId,
            releaseStepType,
            workflowId,
            workflowRunId,
            stepRunIds,
        }),
        [orgId, appId, releaseId, releaseStepType, workflowId, workflowRunId, stepRunIds],
    );

    const workflowsQuery = useGetTimelineEventsQuery(baseRequest, {
        skip: !initialLoad || !isWorkflowsRequest || isViewingAppsInDemoMode,
    });

    const legacyQuery = useGetTimelineEventsHistoryQuery(baseRequest, {
        skip: !initialLoad || isWorkflowsRequest || isViewingAppsInDemoMode,
    });

    const [fetchMoreWorkflows, workflowsLazyResult] = useLazyGetTimelineEventsQuery();
    const [fetchMoreLegacy, legacyLazyResult] = useLazyGetTimelineEventsHistoryQuery();

    const {
        data: initialData,
        isLoading: isInitialLoading,
        refetch: refetchInitial,
    } = isWorkflowsRequest ? workflowsQuery : legacyQuery;

    const fetchMore = isWorkflowsRequest ? fetchMoreWorkflows : fetchMoreLegacy;
    const { data: moreData, isLoading: isLazyLoading } = isWorkflowsRequest ? workflowsLazyResult : legacyLazyResult;

    useEffect(() => {
        if (isViewingAppsInDemoMode) {
            const demoEvents =
                release ?
                    (release.timelineEventsHistory ?? [])
                :   appReleases.reduce<TimelineEvent[]>(
                        (events, appRelease) => events.concat(appRelease.timelineEventsHistory ?? []),
                        [],
                    );

            setAllEvents(
                demoEvents.filter((event) => {
                    const isForReleaseStep =
                        releaseStepType ? event.releaseStepArchetypes.includes(releaseStepType) : true;
                    if (!event.stepRunId || !stepRunIds?.length) {
                        return isForReleaseStep;
                    }

                    return isForReleaseStep && stepRunIds.includes(event.stepRunId);
                }),
            );
            setSuccess(true);
        }
    }, [isViewingAppsInDemoMode, release, appReleases]);

    useEffect(() => {
        if (initialData?.data) {
            setAllEvents(initialData.data.events);
            setPaginationInfo(initialData.data.paginationInfo);
        }

        setSuccess(!initialData?.errorMessage?.length);
    }, [initialData]);

    useEffect(() => {
        if (moreData?.data) {
            const newEvents = moreData.data.events;
            const updatedEvents = sortCombinedEvents(allEvents, newEvents);

            setAllEvents(updatedEvents);
            setPaginationInfo(moreData.data.paginationInfo);
            setIsLoadingMore(false);
        }

        setSuccess(!moreData?.errorMessage?.length);
    }, [moreData]);

    const loadMore = useCallback(() => {
        if (!paginationInfo || isLoadingMore || !paginationInfo.hasMore) return;

        setIsLoadingMore(true);

        fetchMore({
            ...baseRequest,
            nextCursor: paginationInfo.nextCursor,
        });
    }, [paginationInfo, isLoadingMore, fetchMore, baseRequest]);

    const refresh = useCallback(() => {
        setIsLoadingMore(false);
        setSuccess(true);
        refetchInitial();
    }, [refetchInitial]);

    const reset = useCallback(() => {
        setAllEvents([]);
        setSuccess(true);
        setIsLoadingMore(false);
    }, []);

    return {
        events: allEvents ?? [],
        paginationInfo: paginationInfo,
        isLoading: isInitialLoading,
        isLoadingMore: isLoadingMore || isLazyLoading,
        error: !success,
        loadMore,
        refresh,
        reset,
    };
};

const sortCombinedEvents = (allEvents: TimelineEvent[], newEvents: TimelineEvent[]) => {
    const updatedEvents = [...allEvents, ...newEvents];

    if (newEvents.length && allEvents.length) {
        const newestDate = dayjs(newEvents[0].triggeredAt);
        const oldestDate = dayjs(allEvents[allEvents.length - 1].triggeredAt);

        if (newestDate.isAfter(oldestDate)) {
            updatedEvents.sort((a, b) => dayjs(b.triggeredAt).diff(a.triggeredAt));
        }
    }

    return updatedEvents;
};
